import { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Top-level error boundary that catches any uncaught errors,
 * including Convex errors that may occur outside route boundaries.
 */
class ConvexGlobalErrorHandler extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ConvexGlobalErrorHandler caught error:', error, errorInfo)

    // Check if this is a 2FA verification error
    const is2FAError =
      error.message?.includes('2FA verification required') ||
      error.message?.includes('Not authenticated')

    if (is2FAError) {
      console.log('2FA session expired globally, redirecting to login...')
      // Redirect to login page
      window.location.href = '/admin/login'
      return
    }

    // Handle token refresh failures
    const isTokenRefreshError =
      error instanceof TypeError &&
      error.message?.includes('Failed to fetch') &&
      (errorInfo.componentStack?.includes('ConvexAuthProvider') ||
        error.stack?.includes('@convex-dev/auth'))

    if (isTokenRefreshError) {
      console.log(
        'Token refresh failed in error boundary, clearing session and reloading...',
      )
      localStorage.removeItem('convex-auth')
      sessionStorage.clear()
      window.location.reload()
    }
  }

  public render() {
    if (this.state.hasError) {
      const is2FAError =
        this.state.error?.message?.includes('2FA verification required') ||
        this.state.error?.message?.includes('Not authenticated')

      const isTokenRefreshError =
        this.state.error instanceof TypeError &&
        this.state.error?.message?.includes('Failed to fetch')

      if (is2FAError || isTokenRefreshError) {
        // Return null while redirecting/reloading
        return null
      }

      // For other errors, show a fallback UI
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-white p-8">
          <div className="max-w-md text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-700 mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null })
                window.location.reload()
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ConvexGlobalErrorHandler
